import { useCallback, useEffect, useRef } from "react";
import { clientFetch } from "@flights/client-fetch";
import useContextName from "./useContextName.js";
import usePointOfSale from "./usePointOfSale.js";
import useGlobalContext from "./useGlobalContext.js";
const debug = require("debug")("useClientFetch");
const CACHE_TIMEOUT = 1000 * 60 * 3;
const cache = {};
export const useClientFetch = (url, options) => {
    const { isSnapshot, isInternal } = useGlobalContext();
    const { clientFetchCallback } = useClientFetchCallback();
    useEffect(() => {
        if (process.env.BUILD_TARGET !== "client")
            return;
        const { fetch: fetchStart, success: fetchSuccess, error: fetchError, finally: fetchFinally, condition, delayFetchBy = 0, forceRefetch } = options;
        const fetchData = () => {
            fetchStart?.(url);
            debug(`Fetching ${absoluteURL}`);
            clientFetchCallback(url, { ...options, cache: !forceRefetch })
                .then(fetchSuccess)
                .catch((error) => {
                fetchError(error.code ? error : { message: error.toString() });
            })
                .finally(() => {
                fetchFinally?.();
            });
        };
        const absoluteURL = /^http/.test(url) ? url : `${location.protocol}//${location.host}${url}`;
        if (isSnapshot && isInternal) {
            return;
        }
        if (typeof condition === "function" && condition() != true) {
            debug(`Skipping fetch for ${absoluteURL}`);
            return;
        }
        if (delayFetchBy) {
            setTimeout(() => {
                fetchData();
            }, delayFetchBy);
        }
        else {
            fetchData();
        }
        return () => {
            debug(`Cleaning up after ${absoluteURL}`);
        };
    }, [options.method, options.forceRefetch, options.signal, url]);
};
function pushToCache(url, obj) {
    const keys = Object.keys(cache);
    if (keys.length > 5) {
        delete cache[keys[0]];
    }
    cache[url] = obj;
    setTimeout(() => {
        cache[url] && delete cache[url];
    }, CACHE_TIMEOUT);
}
function fetchWithCache(url, options) {
    const { headers, method, body, signal } = options;
    const cachedData = cache[url];
    if (options.cache !== false && cachedData) {
        return Promise.resolve(cachedData);
    }
    return clientFetch(url, { headers, method, body, signal }).then((data) => {
        pushToCache(url, data);
        return data;
    });
}
export const useClientFetchCallback = () => {
    const contextName = useContextName();
    const posCountry = useRef(usePointOfSale());
    const clientFetchCallback = useCallback((url, options) => {
        const { headers = {}, shouldGenerateNewClientsidePayload, forceRefetch } = options;
        headers["X-Flights-Context-Name"] = contextName;
        if (shouldGenerateNewClientsidePayload)
            headers["X-Booking-Experiment-Prepare-Clientside-Payload"] = "1";
        if (posCountry.current)
            headers["X-Flights-Context-POS"] = posCountry.current;
        return fetchWithCache(url, { ...options, cache: !forceRefetch, headers });
    }, [posCountry, contextName]);
    return { clientFetchCallback };
};
